/**
 * useCvTranslation
 *
 * Owns the "translate a CV version" flow:
 * - translatePopoverId    : which version's "translate" popover is open
 * - isTranslatingVersionId: id of the version currently being translated
 * - translateVersion()    : clone a version, translate it via AI, persist
 *                            the result as a new linked version, and switch
 *                            the active template to it.
 *
 * Uses the shared client-cache (`cv_translate_<lang>_structured` +
 * `cv_translate_<lang>_fp`) to avoid re-translating identical CVs.
 *
 * Takes the useCvState setters as params so that creating the new version
 * can happen without cross-hook coupling.
 */

import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import type { CVData } from '../types';
import { parseJsonResponse } from '../lib/fetch-utils';
import {
  createCvFingerprint,
  getCachedStep,
  setCachedStep,
} from '../lib/client-cache';
import { cloneCvData, makeTemplateId } from '../lib/cv-utils';
import type { TemplateMetaItem } from './useCvState';
import { toast } from '../lib/toast';

export interface UseCvTranslationParams {
  templateMeta: TemplateMetaItem[];
  allTemplatesData: Record<string, CVData>;
  /** CVData → plain text, supplied by the caller (depends on i18n labels). */
  getResumeText: (cv: CVData) => string;
  setAllTemplatesData: Dispatch<SetStateAction<Record<string, CVData>>>;
  setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;
  setActiveTemplate: Dispatch<SetStateAction<string>>;
}

export interface UseCvTranslationReturn {
  translatePopoverId: string | null;
  setTranslatePopoverId: Dispatch<SetStateAction<string | null>>;
  isTranslatingVersionId: string | null;
  translateVersion: (sourceId: string, targetLang: 'fr' | 'en') => Promise<void>;
}

function buildTranslatedName(originalName: string, targetLang: 'fr' | 'en'): string {
  const suffix = targetLang === 'en' ? ' (EN)' : ' (FR)';
  const base =
    originalName.length + suffix.length > 60
      ? originalName.slice(0, 60 - suffix.length)
      : originalName;
  return base + suffix;
}

export function useCvTranslation(params: UseCvTranslationParams): UseCvTranslationReturn {
  const {
    templateMeta,
    allTemplatesData,
    getResumeText,
    setAllTemplatesData,
    setTemplateMeta,
    setActiveTemplate,
  } = params;

  const [translatePopoverId, setTranslatePopoverId] = useState<string | null>(null);
  const [isTranslatingVersionId, setIsTranslatingVersionId] = useState<string | null>(null);

  const commitTranslation = useCallback(
    (
      sourceId: string,
      sourceName: string,
      targetLang: 'fr' | 'en',
      translated: CVData,
    ) => {
      const srcData = allTemplatesData[sourceId];
      if (srcData?.personalInfo?.avatar) {
        // Preserve the original avatar across the translation.
        translated.personalInfo = {
          ...translated.personalInfo,
          avatar: srcData.personalInfo.avatar,
        };
      }
      const existingIds = templateMeta.map((t) => t.id);
      const name = buildTranslatedName(sourceName, targetLang);
      const newId = makeTemplateId(name, existingIds);
      setAllTemplatesData((prev) => ({ ...prev, [newId]: translated }));
      const sourceLang: 'fr' | 'en' = targetLang === 'en' ? 'fr' : 'en';
      setTemplateMeta((prev) => [
        ...prev.map((t) =>
          t.id === sourceId ? { ...t, lang: sourceLang, linkedVersionId: newId } : t,
        ),
        { id: newId, name, lang: targetLang, linkedVersionId: sourceId },
      ]);
      setActiveTemplate(newId);
    },
    [allTemplatesData, templateMeta, setAllTemplatesData, setTemplateMeta, setActiveTemplate],
  );

  const translateVersion = useCallback(
    async (sourceId: string, targetLang: 'fr' | 'en') => {
      const sourceMeta = templateMeta.find((t) => t.id === sourceId);
      if (!sourceMeta) return;
      const sourceData = allTemplatesData[sourceId];
      if (!sourceData) return;

      setTranslatePopoverId(null);
      setIsTranslatingVersionId(sourceId);

      // CV-only fingerprint (translate does not depend on any job offer).
      const fingerprint = createCvFingerprint(sourceId, getResumeText(sourceData));
      const cacheKey = `cv_translate_${targetLang}_structured`;
      const fpKey = `cv_translate_${targetLang}_fp`;

      // Cache hit → no network round-trip.
      const cached = getCachedStep<CVData>(cacheKey, fpKey, fingerprint);
      if (cached) {
        commitTranslation(sourceId, sourceMeta.name, targetLang, cloneCvData(cached.data));
        setIsTranslatingVersionId(null);
        return;
      }

      try {
        const res = await fetch('/api/ai', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            route: 'ats-optimize',
            action: 'translate',
            cvData: sourceData,
            targetLang,
          }),
        });
        const json = await parseJsonResponse<{ error?: string; data?: CVData }>(res);
        if (!res.ok) throw new Error(json.error || 'Erreur traduction');
        const translated = cloneCvData(json.data!);
        setCachedStep(cacheKey, fpKey, fingerprint, translated);
        commitTranslation(sourceId, sourceMeta.name, targetLang, translated);
      } catch (e) {
        console.error('[Translate version]', e);
        toast.error(e instanceof Error ? e.message : 'Erreur traduction');
      } finally {
        setIsTranslatingVersionId(null);
      }
    },
    [templateMeta, allTemplatesData, getResumeText, commitTranslation],
  );

  return {
    translatePopoverId,
    setTranslatePopoverId,
    isTranslatingVersionId,
    translateVersion,
  };
}
